import { useCallback, useEffect, useRef, useState } from 'react'
import type { PopperProps } from '@mui/material'
import type React from 'react'

const HOVER_CLOSE_DELAY_MS = 150

type Placement = NonNullable<PopperProps['placement']>

export type PinnablePopoverHandlers = {
  anchorEl: HTMLElement | null
  isOpen: boolean
  lockedPlacement: Placement | undefined
  lockPlacement: (placement: Placement | undefined) => void
  close: () => void
  handleClick: (event: React.MouseEvent<HTMLElement>) => void
  handleTriggerEnter: (event: React.SyntheticEvent<HTMLElement>) => void
  scheduleClose: () => void
  cancelClose: () => void
  handleDialogKeyDown: (event: React.KeyboardEvent) => void
  dialogRef: (el: HTMLDivElement | null) => void
}

export const usePinnablePopover = (): PinnablePopoverHandlers => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [lockedPlacement, setLockedPlacement] = useState<Placement>()
  const isPinned = useRef(false)
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)

  const anchorElRef = useRef(anchorEl)
  useEffect(() => {
    anchorElRef.current = anchorEl
  }, [anchorEl])

  const dialogElRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    clearTimeout(closeTimeout.current)
    const wasPinned = isPinned.current
    isPinned.current = false
    if (wasPinned) anchorElRef.current?.focus()
    setAnchorEl(null)
    setLockedPlacement(undefined)
  }, [])

  const scheduleClose = useCallback(() => {
    if (isPinned.current) return
    clearTimeout(closeTimeout.current)
    closeTimeout.current = setTimeout(close, HOVER_CLOSE_DELAY_MS)
  }, [close])

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimeout.current)
  }, [])

  useEffect(() => () => clearTimeout(closeTimeout.current), [])

  const dialogRef = useCallback((el: HTMLDivElement | null) => {
    dialogElRef.current = el
    if (el && isPinned.current) el.focus()
  }, [])

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    isPinned.current = true
    clearTimeout(closeTimeout.current)
    setAnchorEl(event.currentTarget)
    queueMicrotask(() => dialogElRef.current?.focus())
  }, [])

  const handleTriggerEnter = useCallback((event: React.SyntheticEvent<HTMLElement>) => {
    if (!isPinned.current) {
      clearTimeout(closeTimeout.current)
      anchorElRef.current = event.currentTarget
      setAnchorEl(event.currentTarget)
    }
  }, [])

  const handleDialogKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close()
      }
    },
    [close]
  )

  return {
    anchorEl,
    isOpen: Boolean(anchorEl),
    lockedPlacement,
    lockPlacement: setLockedPlacement,
    close,
    handleClick,
    handleTriggerEnter,
    scheduleClose,
    cancelClose,
    handleDialogKeyDown,
    dialogRef,
  }
}
